import { useEffect, useRef } from "react";

type Getter<T> = () => T;
type Setter<T> = (value: T) => void;

type ComputedOptions<T> = {
  get: Getter<T>;
  set?: Setter<T>;
};

type ComputedRefCompat<T> = {
  (): T;
  readonly value: T;
  current: T;
};

type RefCompat<T> = {
  value: T;
  current: T;
};

type WatchSource<T = unknown> = T | RefCompat<T> | (() => T);
type WatchCallback<T = unknown> = (newValue: T, oldValue: T | undefined) => void;
type WatchOptions = {
  immediate?: boolean;
};

function defineCurrentAlias<T extends object>(target: T, get: Getter<any>, set: Setter<any>): T {
  Object.defineProperty(target, "current", {
    enumerable: false,
    configurable: true,
    get,
    set
  });
  return target;
}

export function ref<T>(value: T): RefCompat<T>;
export function ref<T = null>(value?: T): RefCompat<T | null>;
export function ref<T = null>(value?: T): RefCompat<T | null> {
  const state: RefCompat<T | null> = {
    value: (value ?? null) as T | null,
    current: null as T | null
  };
  return defineCurrentAlias(
    state,
    () => state.value,
    next => {
      state.value = next;
    }
  );
}

export const shallowRef = ref;

export function readonly<T>(value: T): T {
  return value;
}

export function isRef(value: unknown): value is { value?: unknown; current?: unknown } {
  if (!value || (typeof value !== "object" && typeof value !== "function")) return false;
  const record = value as Record<string, unknown>;
  return "value" in record || "current" in record;
}

function hasValue(value: unknown): value is { value: unknown } {
  return Boolean(
    value &&
    (typeof value === "object" || typeof value === "function") &&
    "value" in (value as Record<string, unknown>)
  );
}

export function unref<T>(value: T | { value: T }): T {
  if (!isRef(value)) return value as T;
  if (hasValue(value)) return (value as { value: T }).value;
  return (value as { current: T }).current;
}

export function computed<T>(getter: Getter<T> | ComputedOptions<T>): ComputedRefCompat<T> {
  const get: Getter<T> = typeof getter === "function" ? getter : getter.get;
  const set: Setter<T> =
    typeof getter === "function"
      ? () => {
          throw new Error("Write operation failed: computed value is readonly");
        }
      : (getter.set ??
        (() => {
          throw new Error("Write operation failed: computed value is readonly");
        }));

  const callable = (() => get()) as ComputedRefCompat<T>;

  Object.defineProperty(callable, "value", {
    enumerable: true,
    configurable: true,
    get,
    set
  });

  return defineCurrentAlias(callable, get, set);
}

export function reactive<T extends object>(obj: T): T {
  return obj;
}

export function toRef<T extends object, K extends keyof T>(obj: T, key: K): RefCompat<T[K]> {
  const state: RefCompat<T[K]> = {
    get value() {
      return obj[key];
    },
    set value(next: T[K]) {
      obj[key] = next;
    },
    current: obj[key]
  };
  return defineCurrentAlias(
    state,
    () => obj[key],
    next => {
      obj[key] = next;
    }
  );
}

export function toRefs<T extends object>(obj: T): { [K in keyof T]: RefCompat<T[K]> } {
  const out = {} as { [K in keyof T]: RefCompat<T[K]> };
  for (const key of Object.keys(obj) as (keyof T)[]) {
    out[key] = toRef(obj, key);
  }
  return out;
}

function readWatchSource(source: any): any {
  if (typeof source === "function") return source();
  if (Array.isArray(source)) return source.map(readWatchSource);
  return unref(source);
}

export function useVueWatch<T = unknown>(
  source: WatchSource<T> | WatchSource<T>[],
  cb: WatchCallback<T | T[]>,
  optionsOrDeps?: WatchOptions | unknown[],
  deps?: unknown[]
): void {
  const options = Array.isArray(optionsOrDeps) ? undefined : optionsOrDeps;
  const resolvedDeps = Array.isArray(optionsOrDeps) ? optionsOrDeps : deps;

  const stateRef = useRef<{ initialized: boolean; value: T | T[] | undefined }>({
    initialized: false,
    value: undefined
  });

  const sourceValue = readWatchSource(source) as T | T[];
  const effectDeps = resolvedDeps ?? [sourceValue];

  useEffect(() => {
    const nextValue = readWatchSource(source) as T | T[];
    const previousValue = stateRef.current.value;
    const isFirstRun = !stateRef.current.initialized;

    if (isFirstRun) {
      stateRef.current.initialized = true;
      stateRef.current.value = nextValue;
      if (options?.immediate) {
        cb(nextValue, previousValue);
      }
      return;
    }

    if (Object.is(previousValue, nextValue)) return;
    stateRef.current.value = nextValue;
    cb(nextValue, previousValue);
  }, effectDeps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function watch<T = unknown>(
  source: WatchSource<T> | WatchSource<T>[],
  cb: WatchCallback<T | T[]>,
  optionsOrDeps?: WatchOptions | unknown[],
  deps?: unknown[]
): () => void {
  const runVueWatch = useVueWatch;
  runVueWatch(source, cb, optionsOrDeps, deps);
  return () => {};
}

export function watchEffect(effect: () => void): () => void {
  useEffect(effect);
  return () => {};
}

export function onMounted(cb: () => void): void {
  useEffect(cb, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function onUnmounted(cb: () => void): void {
  useEffect(() => cb, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function onBeforeUnmount(cb: () => void): void {
  useEffect(() => cb, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function nextTick<T>(cb?: () => T): Promise<T | void> {
  return Promise.resolve().then(() => cb?.());
}
